// AiScopeSwitch / AiScopeChip — THE CONTEXT of an AI ask, chosen in the
// composer's focus panel (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1).
//
// One radio group: the open record, the page, the app, the workspace, then the
// other installed apps — most specific first (the shell's context model,
// workspace functions/aiScope.js, through `el.call`). Each choice is a chip:
// the context's line glyph and its short name; the chosen one is the D-1
// inversion (`highlight` fill, `highlight-reversed` ink) the kit's choice
// chips wear (AppChoiceChip), every other one the tonal capsule of its own ink
// (14 / 22 / 30 % on rest / hover / press — Rule 67, a tint of currentColor
// follows the theme). The row WRAPS (overflow law §3b.5: nothing is cut at
// the edge; a long Georgian app name takes a second line, never an ellipsis).
//
// THE KEYBOARD (the radio group pattern): one Tab stop — the chosen chip
// (roving tabindex); ← → ↑ ↓ move the choice and the focus, Home / End reach
// the ends. The move IS the choice (automatic activation), like the kit's
// segmented control (AppSegmentedItem). A chip's name says its kind: "Page:
// Validators · Overview".
//
// The host passes nothing: the rows, the chosen key and the writer are the
// shell's (`aiScopeOptions`, `aiScopeChosenKey`, `setAiScope`). A host
// without them renders no switch.
export const AiScopeSwitch = {
  role: 'radiogroup',
  ariaLabel: (el) => el.call('polyglot', 'ai.scope.label'),
  hide: (el) => !(el.context && el.context.functions && el.context.functions.aiScopeOptions),
  flow: 'x',
  flexWrap: 'wrap',
  align: 'center start',
  gap: 'Y',
  minWidth: '0',
  childExtends: 'AiScopeChip',
  childrenAs: 'state',
  children: (el, s) => {
    const fns = el.context && el.context.functions
    if (!fns || typeof fns.aiScopeOptions !== 'function') return []
    let rows = []
    try {
      rows = el.call('aiScopeOptions', s.root) || []
    } catch (_) {
      rows = []
    }
    return rows.map((r) => ({
      key: 'scope-' + r.key,
      value: r.key,
      text: r.short || r.label,
      label: r.label,
      kind: r.kind,
      other: !!r.other,
      glyph: r.glyph
    }))
  }
}

export const AiScopeChip = {
  tag: 'button',
  type: 'button',
  role: 'radio',
  flow: 'x',
  align: 'center',
  gap: 'X',
  flexShrink: '0',
  maxWidth: '100%',
  minHeight: 'controlHCompact',
  boxSizing: 'border-box',
  padding: '0 Z1 0 Y',
  round: 'radiusPill',
  borderWidth: '0',
  cursor: 'pointer',
  fontSize: 'Z',
  fontWeight: '500',
  lineHeight: '1.2',
  textAlign: 'left',
  color: 'title',
  background: 'color-mix(in srgb, currentColor 14%, transparent)',
  transition: 'background-color A defaultBezier, color A defaultBezier',
  ':hover': { background: 'color-mix(in srgb, currentColor 22%, transparent)' },
  ':active': { background: 'color-mix(in srgb, currentColor 30%, transparent)' },
  '@coarse': { minHeight: 'controlH' },

  scope: {
    // The chosen key, read through the shell's one reader.
    chosen(el, s) {
      try {
        return el.call('aiScopeChosenKey', s && s.root)
      } catch (_) {
        return null
      }
    },
    // A chip's spoken name: its kind, then its full name ("Page: Validators ·
    // Overview") — the colon form, so a translation never declines the name.
    spoken(el, s) {
      const kinds = {
        record: 'ai.scope.kind.record',
        page: 'ai.scope.kind.page',
        app: 'ai.scope.kind.app',
        workspace: 'ai.scope.kind.workspace'
      }
      const kind = el.call('polyglot', kinds[s && s.kind] || kinds.app)
      return kind + ': ' + ((s && (s.label || s.text)) || '')
    },
    // Move the choice to a sibling chip and its focus with it.
    move(el, to) {
      const host = el.parent
      const items = host && typeof host.getChildren === 'function' ? host.getChildren() : []
      if (!items.length) return
      const at = items.indexOf(el)
      const i =
        to === 'first'
          ? 0
          : to === 'last'
            ? items.length - 1
            : (at + to + items.length) % items.length
      const next = items[i]
      if (!next || next === el) return
      el.call('setAiScope', next.state && next.state.value)
      if (next.node && typeof next.node.focus === 'function') next.node.focus()
    }
  },

  isActive: (el, s) => el.scope.chosen(el, s) === (s && s.value),
  ariaChecked: (el, s) => (el.scope.chosen(el, s) === (s && s.value) ? 'true' : 'false'),
  tabindex: (el, s) => (el.scope.chosen(el, s) === (s && s.value) ? '0' : '-1'),
  ariaLabel: (el, s) => el.scope.spoken(el, s),
  title: (el, s) => (s && s.label) || null,
  dataActive: (el, s) => el.scope.chosen(el, s) === (s && s.value),
  '&[data-active]': { background: 'highlight', color: 'highlight-reversed' },
  '&[data-active]:hover': { background: 'highlight', color: 'highlight-reversed' },
  // WINDOWS CONTRAST: forced colours drop the fills — the chosen chip keeps a
  // system outline (the kit segment's rule).
  '@forcedColors': {
    '&[data-active]': {
      outlineWidth: 'hairline2',
      outlineStyle: 'solid',
      outlineColor: 'Highlight',
      outlineOffset: '0'
    }
  },

  // The context's line glyph — never shrinks, the name does (it wraps).
  Icon: {
    name: (el, s) => (s && s.glyph) || 'lnApps',
    boxSize: 'iconSm',
    flexShrink: '0',
    ariaHidden: 'true'
  },
  Label: {
    tag: 'span',
    minWidth: '0',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.text) || ''
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('setAiScope', s && s.value)
  },
  onKeydown: (e, el) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]
    const to = step || (e.key === 'Home' ? 'first' : e.key === 'End' ? 'last' : null)
    if (!to) return
    e.preventDefault()
    e.stopPropagation()
    el.scope.move(el, to)
  }
}
